import { useContext, useState } from 'react';
import {
  Anchor,
  Box,
  Heading,
  IconButton,
  Menu,
  Paragraph,
  Text,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import { IconChevronLeftOutline, IconFileOutline } from '@zillow/constellation-icons';
import { RentalTotalPriceLabel } from '@zillow/rental-total-price-components';
import { ConversationActions, ReportConversationModal } from '@zg-rentals/satellite-components';
import {
  useSatelliteWebRenterConversationHandler_updateStatus,
  useSatelliteWebRenterLeadHandler_requestToApply,
} from '@zg-rentals/ts-satellite-client-react-query';
import { AppContext } from '../../contexts/AppContextProvider';
import {
  APPLY_NOW_CLICK_EVENT_TYPE_ID,
  emitApplyNowClickEvent,
} from '../../constants/clickstreamEvents/applyToListing';
import StringConstants from '../../constants/StringConstants';
import { isHotpads, isZillow } from '@zg-rentals/multibrand-support';
import type { Brand } from '@zg-rentals/multibrand-support';
import type { Rh_SyndicationUrls } from '../../types/GraphqlTypes';
import { ReportConversationReason } from '../../types/GraphqlTypes';
import { Folder } from '../../types/GraphqlTypes';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import PropertyThumbnailWithAvatar from '../PropertyThumbnailWithAvatar';
import Skeleton from './Skeleton';
import {
  emitOpenReportProblemModalEvent,
  reportReasonClickstreamEventMap,
} from '../../constants/clickstreamEvents/reportProblemModal';
import useInboxNextApolloHelper from '../../hooks/useInboxNextApolloHelper';
import useGetFeatureFlagsAndSettings from '../../hooks/useGetFeatureFlagsAndSettings';
import { logError } from '@zg-rentals/log-error';
import { FULLSTORY_EXCLUDE_CLASS_NAME } from '@zg-rentals/satellite-components';
import {
  emitRachelArchiveConversationEvent,
  emitRachelUnarchiveConversationEvent,
} from '../../constants/clickstreamEvents/conversationActions';
import { useBrandedUserData } from '../../hooks/useBrandedUserData';
import { bulletSeparatedListStyles, conversationNavWrapperStyles, totalPriceLabelWrapperStyles } from './styles';
import { MultiBrandSupportUserReportConversationReasonType } from '@zg-rentals/ts-satellite-client/src/types/multibrandsupport/MultiBrandSupportUserReportConversationReasonType';

type ReportConversationArgs = {
  reason: ReportConversationReason;
  comments?: string;
};

type Props = {
  refetchConversationList: ({ conversationIdToRedirectTo }: { conversationIdToRedirectTo?: string }) => void;
  showConversationListOnMobile(): void;
  onToastUndoClick(conversationId: string): Promise<void>;
};

const getSyndicationUrlKey = (brand: Brand): keyof Rh_SyndicationUrls => {
  switch (brand) {
    case StringConstants.UNFORMATTED_BRAND_NAMES.zillow:
      return 'zillowUrl';
    case StringConstants.UNFORMATTED_BRAND_NAMES.hotpads:
      return 'hotpadsUrl';
    case StringConstants.UNFORMATTED_BRAND_NAMES.trulia:
      return 'truliaUrl';
    default:
      return 'hotpadsUrl';
  }
};

const ConversationNav = ({
  refetchConversationList,
  showConversationListOnMobile,
  onToastUndoClick,
}: Props): JSX.Element => {
  const [isReportConversationModalOpen, setIsReportConversationModalOpen] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { enqueueToast } = useToast();
  const { brand, isImpersonator, isInWebView } = useContext(AppContext);
  const { user } = useBrandedUserData();
  const { conversationId, folder } = useContext(InboxPageContext);
  const { features, settings } = useGetFeatureFlagsAndSettings();
  const { enableTMP } = features;
  const { abTreatmentForTMP } = settings;
  const isZillowBrand = isZillow(brand);
  const isHotpadsBrand = isHotpads(brand);

  const ToastTextByAction: Record<ConversationActionTypes, string> = {
    [ConversationActionTypes.Archive]: isZillowBrand ? 'Moved to archived' : 'Conversation archived',
    [ConversationActionTypes.Report]: isZillowBrand ? 'Moved to spam' : 'Conversation reported',
    [ConversationActionTypes.MoveToInbox]: isZillowBrand ? 'Moved to recent' : 'Conversation moved to inbox',
    [ConversationActionTypes.SuccessfulRequestToApply]: 'Conversation marked as unread',
  };

  const { useConversationNavRhGetConversationByIdDocumentQuery, useRHGetSyndicationUrlsQuery } =
    useInboxNextApolloHelper();
  const {
    data: conversationData,
    loading: isLoadingConversationData,
    error,
  } = useConversationNavRhGetConversationByIdDocumentQuery({ conversationId });

  const { mutateAsync: updateConversationStatus } = useSatelliteWebRenterConversationHandler_updateStatus({
    meta: {
      context: {
        api: 'updateStatus',
        details: 'ConversationNav - Error updating conversation status',
      },
    },
  });

  const { mutateAsync: requestToApply } = useSatelliteWebRenterLeadHandler_requestToApply({
    meta: {
      context: {
        api: 'requestToApply',
        details: 'ConversationNav - Error requesting to apply',
      },
    },
  });

  const {
    address,
    application,
    beds,
    isApplicationEnabledByLandlord,
    isApplicationsAllowed,
    isConversationBlockedByZillow,
    isMultifamily,
    landlordName,
    numBathroomsHigh,
    photoUrl,
    pricing,
    referenceEmail,
    listingAlias,
    listingName,
    addressDetails,
    pricingDetails,
    pricingDisplay,
  } = conversationData?.RH_getConversationById || {};
  const hasApplicationId = !!application?.applicationId;
  const { stateAbbrev } = addressDetails || {};
  const { isHasBaseRentAndFees, baseRentMin, baseRentMax, totalRequiredMonthlyFeeMin, totalRequiredMonthlyFeeMax } =
    pricingDetails || {};

  // TODO: Use AB test value from the browser window's object once it's available
  const shouldDisplayTMP = !!enableTMP && pricingDisplay !== 'LEGACY';
  const abTreatmentValueForTMP = shouldDisplayTMP ? abTreatmentForTMP : 'CONTROL';
  const shouldDisplayBathrooms = numBathroomsHigh && !isMultifamily;

  const {
    data: syndicationUrlsData,
    loading: isLoadingSyndicationUrlsData,
    error: syndicationError,
  } = useRHGetSyndicationUrlsQuery({ listingAlias: listingAlias?.toString(), brand });

  if (syndicationError) {
    logError({
      error: syndicationError,
      errorType: 'SyndicationError',
      context: { info: 'Error loading syndication urls', listingAlias, referenceEmail, conversationId },
    });
  }

  const syndicationUrlKey = getSyndicationUrlKey(brand);
  const propertyUrl = syndicationUrlsData?.RH_getSyndicationUrls?.[syndicationUrlKey] || '';
  const hasPropertyLink = !!propertyUrl;
  const showRequestToApply =
    isApplicationsAllowed && hasPropertyLink && !isApplicationEnabledByLandlord && !isMultifamily && isHotpadsBrand;
  const showApplyNow = isApplicationsAllowed && isApplicationEnabledByLandlord;
  const listingAddressDisplay = address?.split(',')[0] || '';
  const listingDisplayName = listingName || listingAddressDisplay;
  const avatarName = landlordName && !isLoadingConversationData ? landlordName : '';
  const avatarPhotoSrc = photoUrl && !isLoadingConversationData ? photoUrl : '';

  const handleBackArrowClick = (): void => showConversationListOnMobile();

  const handlePropertyClick = (): void => {
    window.open(propertyUrl, '_blank');
  };

  // TODO: This mapping is temporary during React Query migration from GraphQL to use Proxit API.
  // The ReportConversationModal component (from satellite-components) uses GraphQL enums,
  // but the new Proxit API expects MultiBrandSupportUserReportConversationReasonType.
  // Future work: Refactor satellite-components to use API types directly.
  const mapReportReasonToApiType = (
    reason?: ReportConversationReason,
  ): MultiBrandSupportUserReportConversationReasonType | undefined => {
    if (!reason) return undefined;
    // Map GraphQL enum values to API enum values
    switch (reason) {
      case ReportConversationReason.DiscriminatoryOrAbusive:
        return MultiBrandSupportUserReportConversationReasonType.DISCRIMINATORY_OR_ABUSIVE;
      case ReportConversationReason.NotReported:
        return MultiBrandSupportUserReportConversationReasonType.NOT_REPORTED;
      case ReportConversationReason.Other:
        return MultiBrandSupportUserReportConversationReasonType.OTHER;
      case ReportConversationReason.Spam:
        return MultiBrandSupportUserReportConversationReasonType.SPAM;
      default:
        return undefined;
    }
  };

  const updateConversationStatusByAction = async (
    action: ConversationActionTypes,
    reportConversationReason?: ReportConversationReason,
    userReportComment?: string,
  ): Promise<{ success: boolean } | undefined> => {
    if (!conversationId) return;

    const postData: {
      conversationId: string;
      isArchived?: boolean;
      reportReasonType?: MultiBrandSupportUserReportConversationReasonType;
      userReportComment?: string;
    } = {
      conversationId,
    };

    switch (action) {
      case ConversationActionTypes.Archive:
        postData.isArchived = true;
        break;
      case ConversationActionTypes.Report:
        postData.reportReasonType = mapReportReasonToApiType(reportConversationReason);
        postData.userReportComment = userReportComment;
        break;
      case ConversationActionTypes.MoveToInbox:
        postData.isArchived = false;
        postData.reportReasonType = MultiBrandSupportUserReportConversationReasonType.NOT_REPORTED;
        break;
      default:
        logError({ error: `No case for: ${action}`, errorType: 'StatusUpdateError', context: { action } });
        return;
    }

    try {
      await updateConversationStatus({
        postData,
      });

      return { success: true };
    } catch (err) {
      logError({ error: err, context: { conversationId, info: `Error on ${action} conversation` } });
    }
  };

  const showActionNotification = (action: ConversationActionTypes, conversationIdToUndo: string) => {
    const handleToastUndoClick = () => onToastUndoClick(conversationIdToUndo);

    enqueueToast(
      <Toast
        tone="success"
        actionButton={
          <TextButton onClick={handleToastUndoClick} onImpact>
            Undo
          </TextButton>
        }
      >
        {ToastTextByAction[action]}
      </Toast>,
    );
  };

  const handleArchiveClick = async (): Promise<void> => {
    if (!conversationId) return;

    const action = ConversationActionTypes.Archive;
    const response = await updateConversationStatusByAction(action);

    if (response) {
      refetchConversationList({});
      showActionNotification(action, conversationId);
      emitRachelArchiveConversationEvent();
    }
  };

  const handleMoveToInboxClick = async (): Promise<void> => {
    if (!conversationId) return;

    const action = ConversationActionTypes.MoveToInbox;
    const response = await updateConversationStatusByAction(action);

    if (response) {
      refetchConversationList({});
      showActionNotification(action, conversationId);

      if (folder === Folder.Archived) {
        emitRachelUnarchiveConversationEvent();
      }
    }
  };

  const handleRequestToApply = async (): Promise<void> => {
    try {
      if (!conversationId) return;

      // TODO: Fix renterName to pass renter/applicant name instead of landlordName
      // once we start using the /getMessages endpoint
      const response = await requestToApply({
        postData: {
          listingAlias: listingAlias || '',
          renterName: landlordName || '',
          applicantMessage: "I'd like to request an application for this property.",
        },
      });

      if (response && response.success) {
        showActionNotification(ConversationActionTypes.SuccessfulRequestToApply, conversationId);
      } else {
        throw new Error('Request to apply failed');
      }
    } catch (err) {
      logError({ error: err, context: { conversationId, info: `Error on request to apply for ${referenceEmail}` } });
      enqueueToast(<Toast tone="critical">Error sending application. Please try again.</Toast>);
    }
  };

  const handleEventTracking = () => emitApplyNowClickEvent(user);

  const closeReportConversationModal = (): void => setIsReportConversationModalOpen(false);

  const openReportConversationModal = (): void => {
    emitOpenReportProblemModalEvent(user);
    setIsReportConversationModalOpen(true);
  };

  const handleReportConversation = async ({ reason, comments }: ReportConversationArgs): Promise<void> => {
    if (!conversationId) return;

    const clickstreamEvent =
      reportReasonClickstreamEventMap[
        reason as Exclude<ReportConversationReason, ReportConversationReason.NotReported>
      ];

    clickstreamEvent?.(user);

    const action = ConversationActionTypes.Report;
    const response = await updateConversationStatusByAction(action, reason, comments);

    if (response) {
      refetchConversationList({});
      showActionNotification(action, conversationId);
      closeReportConversationModal();
    }
  };

  const handleCancelClick = (): void => closeReportConversationModal();

  const renderAdditionalMenuItems = (): JSX.Element | null => {
    if (showApplyNow) {
      return (
        <a
          href={`/renter-hub/applications/listing/${listingAlias}/rental-application`}
          onClick={handleEventTracking}
          target="_blank"
          rel="noopener noreferrer"
          data-eventid={APPLY_NOW_CLICK_EVENT_TYPE_ID}
        >
          <Menu.ItemIcon render={<IconFileOutline />} />
          <Menu.ItemLabel>{hasApplicationId ? 'View application' : 'Apply on Zillow'}</Menu.ItemLabel>
        </a>
      );
    }

    if (showRequestToApply) {
      return (
        <button type="button" onClick={handleRequestToApply} disabled={isImpersonator}>
          <Menu.ItemIcon render={<IconFileOutline />} />
          <Menu.ItemLabel>Request to apply</Menu.ItemLabel>
        </button>
      );
    }

    return null;
  };

  if (error) {
    return (
      <Box css={conversationNavWrapperStyles}>
        {!isInWebView && (
          <IconButton
            shape="square"
            emphasis="bare"
            tone="brand"
            icon={<IconChevronLeftOutline />}
            onClick={handleBackArrowClick}
            title="Go back to conversation list"
            size="md"
            css={{
              flexShrink: 0,
              xl: {
                display: 'none',
              },
            }}
          />
        )}
        <Skeleton />
      </Box>
    );
  }

  const additionalMenuItem = renderAdditionalMenuItems();

  return (
    <>
      {isReportConversationModalOpen && (
        <ReportConversationModal
          brand={brand}
          isImpersonator={isImpersonator}
          isOpen={isReportConversationModalOpen}
          name={landlordName || ''}
          onCancelClick={handleCancelClick}
          onReportClick={handleReportConversation}
          persona="renter"
        />
      )}

      <Box css={conversationNavWrapperStyles}>
        {!isInWebView && (
          <IconButton
            shape="square"
            emphasis="bare"
            tone="brand"
            icon={<IconChevronLeftOutline />}
            onClick={handleBackArrowClick}
            title="Go back to conversation list"
            size="md"
            css={{
              flexShrink: 0,
              xl: {
                display: 'none',
              },
            }}
          />
        )}

        <PropertyThumbnailWithAvatar avatarName={avatarName} photoUrl={avatarPhotoSrc} />

        {isLoadingSyndicationUrlsData || isLoadingConversationData ? (
          <Skeleton />
        ) : (
          <Box
            css={{
              display: 'flex',
              flexDirection: 'column',
              marginLeft: 'loose',
              minWidth: 0,
            }}
          >
            <Heading
              level={2}
              textStyle="body-lg-bold"
              css={{
                marginBottom: 'tightest',
                truncate: true,
                color:
                  hasPropertyLink && isZillowBrand
                    ? '{colors.brand}'
                    : hasPropertyLink && isHotpadsBrand
                      ? '#007a70'
                      : '{colors.gray.600}',
              }}
            >
              {hasPropertyLink ? (
                <Anchor asChild>
                  <TextButton onClick={handlePropertyClick}>{listingDisplayName}</TextButton>
                </Anchor>
              ) : (
                <>
                  {listingDisplayName}
                  <Text textStyle="body" css={{ truncate: true, color: 'text.subtle' }}>
                    &nbsp; (Off Market)
                  </Text>
                </>
              )}
            </Heading>

            {landlordName && (
              <Paragraph
                className={FULLSTORY_EXCLUDE_CLASS_NAME}
                textStyle="body-xs"
                css={{ marginBottom: 'tightest', truncate: true }}
              >
                {landlordName}
              </Paragraph>
            )}

            <Box
              css={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0,
                xl: {
                  flexDirection: 'row',
                },
              }}
            >
              {(pricing || shouldDisplayTMP) && (
                <Box css={bulletSeparatedListStyles}>
                  {pricing && <Paragraph textStyle="body-xs">{pricing}</Paragraph>}

                  {shouldDisplayTMP && (
                    <Box data-no-bullet css={totalPriceLabelWrapperStyles}>
                      <RentalTotalPriceLabel
                        appearance="small"
                        abTreatment={abTreatmentValueForTMP || 'ON'}
                        listPriceIncludesRequiredMonthlyFees={!!isHasBaseRentAndFees}
                        minBaseRent={baseRentMin}
                        maxBaseRent={baseRentMax}
                        minMonthlyFee={totalRequiredMonthlyFeeMin}
                        maxMonthlyFee={totalRequiredMonthlyFeeMax}
                        listingState={stateAbbrev}
                        triggerLocation="rachel_inbox"
                        customColor={isHotpadsBrand ? '#007A70' : '#0D4599'}
                        isOpen={isPopoverOpen}
                        onOpen={() => setIsPopoverOpen(true)}
                        onClose={() => setIsPopoverOpen(false)}
                        shouldUseYieldCallback={false}
                      />
                    </Box>
                  )}
                </Box>
              )}

              {(beds || shouldDisplayBathrooms) && (
                <Box
                  css={{
                    ...bulletSeparatedListStyles,
                    marginTop: 'tightest',
                    xl: {
                      marginTop: 0,
                    },
                    // On desktop, add bullet before first child to separate from pricing section
                    '& > *:first-child': {
                      xl: {
                        '&::before': {
                          content: '"•"',
                          marginInline: 'tightest',
                          minWidth: 0,
                        },
                      },
                    },
                  }}
                >
                  {beds && (
                    <Paragraph textStyle="body-xs" css={{ truncate: true }}>
                      {beds}
                    </Paragraph>
                  )}
                  {shouldDisplayBathrooms && (
                    <Paragraph textStyle="body-xs" css={{ truncate: true }}>{`${numBathroomsHigh} bath`}</Paragraph>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        )}

        <Box css={{ marginLeft: 'auto' }}>
          <ConversationActions
            actions={{
              archiveConversation: handleArchiveClick,
              undoArchiveConversation: handleMoveToInboxClick,
              undoReportConversation: handleMoveToInboxClick,
              openReportConversationModal,
            }}
            additionalMenuItem={additionalMenuItem}
            breakpoint="xl"
            isBlocked={isConversationBlockedByZillow || false}
            isImpersonator={isImpersonator}
            menuPlacement="bottom-end"
            shouldHideAdditionalMenuItemsOnDesktop={false}
            shouldShowMarkUnread={false}
            status={{
              isArchived: folder === Folder.Archived,
              isSpam: folder === Folder.Spam,
            }}
            moveToInboxLabel={isZillowBrand ? 'Move to recent' : 'Move to inbox'}
          />
        </Box>
      </Box>
    </>
  );
};

export default ConversationNav;

export enum ConversationActionTypes {
  Archive = 'archive',
  Report = 'report',
  MoveToInbox = 'moveToInbox',
  SuccessfulRequestToApply = 'successfulRequestToApply',
}
